import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { ArrowLeft, UserPlus, Loader2, Trash2, Key, Pencil } from 'lucide-react';
import AuditLinksAdmin from './AuditLinksAdmin';

const AdminPanel = () => {
  const { API, user } = useAuth();
  const navigate = useNavigate();
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);
  const [editingEmployee, setEditingEmployee] = useState(null);
  const [formData, setFormData] = useState({
    employee_id: '', name: '', mobile: '', email: '', password: '', role: 'employee', designation: '', show_on_website: false
  });
  const [editFormData, setEditFormData] = useState({
    employee_id: '', name: '', mobile: '', email: '', password: '', role: 'employee', designation: '', show_on_website: false
  });
  const [photoFile, setPhotoFile] = useState(null);
  const [editPhotoFile, setEditPhotoFile] = useState(null);
  const [passwordData, setPasswordData] = useState({
    current_password: '', new_password: '', confirm_password: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [changingPassword, setChangingPassword] = useState(false);

  useEffect(() => {
    if (user?.role !== 'admin') { navigate('/dashboard'); return; }
    fetchEmployees();
  }, []);

  const fetchEmployees = async () => {
    try {
      const response = await axios.get(`${API}/employees`);
      setEmployees(response.data);
    } catch (error) {
      toast.error('Failed to fetch employees');
    } finally {
      setLoading(false);
    }
  };

  const uploadPhoto = async (empId, file) => {
    const b64 = await new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
    await axios.post(`${API}/employees/${empId}/photo`, { file_name: file.name, file_data: b64, document_type: 'photo' });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const response = await axios.post(`${API}/employees`, formData);
      if (photoFile && response.data?.id) {
        try { await uploadPhoto(response.data.id, photoFile); } catch { toast.error('Employee created, but photo upload failed'); }
      }
      toast.success('Employee added successfully');
      setDialogOpen(false);
      setFormData({ employee_id: '', name: '', mobile: '', email: '', password: '', role: 'employee', designation: '', show_on_website: false });
      setPhotoFile(null);
      fetchEmployees();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add employee');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (employee) => {
    if (!window.confirm(`Permanently delete employee "${employee.name || employee.employee_id}"?\n\nThis action cannot be undone. The Employee ID and email will become available for re-use.`)) return;
    try {
      await axios.delete(`${API}/employees/${employee.id}`);
      toast.success('Employee deleted');
      fetchEmployees();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete employee');
    }
  };

  const openEditDialog = (employee) => {
    setEditingEmployee(employee);
    setEditFormData({
      employee_id: employee.employee_id || '',
      name: employee.name || '',
      mobile: employee.mobile || '',
      email: employee.email || '',
      password: '',
      role: employee.role || 'employee',
      designation: employee.designation || '',
      show_on_website: employee.show_on_website || false,
    });
    setEditPhotoFile(null);
    setEditDialogOpen(true);
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const updates = {};
      if (editFormData.employee_id !== editingEmployee.employee_id) updates.employee_id = editFormData.employee_id;
      if (editFormData.name !== (editingEmployee.name || '')) updates.name = editFormData.name;
      if (editFormData.mobile !== editingEmployee.mobile) updates.mobile = editFormData.mobile;
      if (editFormData.email !== editingEmployee.email) updates.email = editFormData.email;
      if (editFormData.role !== editingEmployee.role) updates.role = editFormData.role;
      if (editFormData.password) updates.password = editFormData.password;
      if (editFormData.designation !== (editingEmployee.designation || '')) updates.designation = editFormData.designation;
      if (editFormData.show_on_website !== (editingEmployee.show_on_website || false)) updates.show_on_website = editFormData.show_on_website;

      if (Object.keys(updates).length === 0 && !editPhotoFile) {
        toast.info('No changes made');
        setEditDialogOpen(false);
        return;
      }

      if (Object.keys(updates).length > 0) {
        await axios.patch(`${API}/employees/${editingEmployee.id}`, updates);
      }
      if (editPhotoFile) {
        await uploadPhoto(editingEmployee.id, editPhotoFile);
      }
      toast.success('Employee updated successfully');
      setEditDialogOpen(false);
      setEditingEmployee(null);
      setEditPhotoFile(null);
      fetchEmployees();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update employee');
    } finally {
      setSubmitting(false);
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    if (passwordData.new_password !== passwordData.confirm_password) {
      toast.error('New passwords do not match'); return;
    }
    if (passwordData.new_password.length < 6) {
      toast.error('New password must be at least 6 characters'); return;
    }
    setChangingPassword(true);
    try {
      await axios.post(`${API}/auth/change-password`, {
        current_password: passwordData.current_password,
        new_password: passwordData.new_password
      });
      toast.success('Password changed successfully');
      setPasswordDialogOpen(false);
      setPasswordData({ current_password: '', new_password: '', confirm_password: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to change password');
    } finally {
      setChangingPassword(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50" data-testid="admin-panel">
      <div className="container mx-auto px-6 py-8">
        <div className="mb-6">
          <Button variant="ghost" onClick={() => navigate('/dashboard')} data-testid="back-to-dashboard-btn">
            <ArrowLeft className="w-4 h-4 mr-2" /> Back to Dashboard
          </Button>
        </div>

        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="text-2xl">Employee Management</CardTitle>
              <div className="flex gap-3">
                <Dialog open={passwordDialogOpen} onOpenChange={setPasswordDialogOpen}>
                  <DialogTrigger asChild>
                    <Button variant="outline" data-testid="change-password-btn">
                      <Key className="w-4 h-4 mr-2" /> Change Password
                    </Button>
                  </DialogTrigger>
                  <DialogContent data-testid="password-form-dialog">
                    <DialogHeader>
                      <DialogTitle>Change Password</DialogTitle>
                      <DialogDescription>Update your account password.</DialogDescription>
                    </DialogHeader>
                    <form onSubmit={handleChangePassword} className="space-y-4">
                      <div className="space-y-2">
                        <Label>Current Password</Label>
                        <Input type="password" value={passwordData.current_password}
                          onChange={(e) => setPasswordData({ ...passwordData, current_password: e.target.value })}
                          required placeholder="Enter current password" data-testid="current-password-input" />
                      </div>
                      <div className="space-y-2">
                        <Label>New Password</Label>
                        <Input type="password" value={passwordData.new_password}
                          onChange={(e) => setPasswordData({ ...passwordData, new_password: e.target.value })}
                          required placeholder="Min 6 characters" data-testid="new-password-input" />
                      </div>
                      <div className="space-y-2">
                        <Label>Confirm New Password</Label>
                        <Input type="password" value={passwordData.confirm_password}
                          onChange={(e) => setPasswordData({ ...passwordData, confirm_password: e.target.value })}
                          required placeholder="Confirm new password" data-testid="confirm-password-input" />
                      </div>
                      <Button type="submit" className="w-full btn-primary" disabled={changingPassword} data-testid="submit-password-btn">
                        {changingPassword && <Loader2 className="w-4 h-4 animate-spin mr-2" />} Change Password
                      </Button>
                    </form>
                  </DialogContent>
                </Dialog>
                <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                  <DialogTrigger asChild>
                    <Button className="btn-primary" data-testid="add-employee-btn">
                      <UserPlus className="w-4 h-4 mr-2" /> Add Employee
                    </Button>
                  </DialogTrigger>
                  <DialogContent data-testid="employee-form-dialog">
                    <DialogHeader>
                      <DialogTitle>Add New Employee</DialogTitle>
                      <DialogDescription>Add a new employee to the system.</DialogDescription>
                    </DialogHeader>
                    <form onSubmit={handleSubmit} className="space-y-4">
                      <div className="space-y-2">
                        <Label>Employee ID</Label>
                        <Input value={formData.employee_id} onChange={(e) => setFormData({ ...formData, employee_id: e.target.value })}
                          required data-testid="employee-id-input" />
                      </div>
                      <div className="space-y-2">
                        <Label>Name</Label>
                        <Input value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                          required placeholder="Full name" data-testid="employee-name-input" />
                      </div>
                      <div className="space-y-2">
                        <Label>Mobile Number</Label>
                        <Input type="tel" value={formData.mobile} onChange={(e) => setFormData({ ...formData, mobile: e.target.value })}
                          required data-testid="mobile-input" />
                      </div>
                      <div className="space-y-2">
                        <Label>Email</Label>
                        <Input type="email" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                          required data-testid="email-input" />
                      </div>
                      <div className="space-y-2">
                        <Label>Password</Label>
                        <Input type="password" value={formData.password} onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                          required placeholder="Enter password" data-testid="password-input" />
                      </div>
                      <div className="space-y-2">
                        <Label>Role</Label>
                        <select value={formData.role} onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                          className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm" data-testid="role-select">
                          <option value="employee">Employee</option>
                          <option value="admin">Admin</option>
                        </select>
                      </div>
                      <div className="space-y-2">
                        <Label>Designation <span className="text-xs text-slate-400">(shown on website)</span></Label>
                        <Input value={formData.designation} onChange={(e) => setFormData({ ...formData, designation: e.target.value })}
                          placeholder="e.g. Fleet Supervisor" data-testid="designation-input" />
                      </div>
                      <div className="space-y-2">
                        <Label>Photo <span className="text-xs text-slate-400">(shown on website)</span></Label>
                        <Input type="file" accept="image/*" onChange={(e) => setPhotoFile(e.target.files?.[0] || null)} data-testid="photo-input" />
                      </div>
                      <div className="flex items-center gap-2">
                        <input type="checkbox" id="show-on-website" checked={formData.show_on_website}
                          onChange={(e) => setFormData({ ...formData, show_on_website: e.target.checked })}
                          className="h-4 w-4 rounded border-gray-300" data-testid="show-on-website-checkbox" />
                        <Label htmlFor="show-on-website" className="cursor-pointer">Show on website (Our Team section)</Label>
                      </div>
                      <Button type="submit" className="w-full btn-primary" disabled={submitting} data-testid="submit-employee-btn">
                        {submitting && <Loader2 className="w-4 h-4 animate-spin mr-2" />} Add Employee
                      </Button>
                    </form>
                  </DialogContent>
                </Dialog>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin" /></div>
            ) : (
              <Table data-testid="employees-table">
                <TableHeader>
                  <TableRow className="table-header">
                    <TableHead>Employee ID</TableHead>
                    <TableHead>Name</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Mobile</TableHead>
                    <TableHead>Role</TableHead>
                    <TableHead>Designation</TableHead>
                    <TableHead>Website</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {employees.map((employee) => (
                    <TableRow key={employee.id} className="table-row">
                      <TableCell className="font-mono">{employee.employee_id}</TableCell>
                      <TableCell className="font-medium">{employee.name || <span className="text-gray-400">—</span>}</TableCell>
                      <TableCell>{employee.email}</TableCell>
                      <TableCell>{employee.mobile}</TableCell>
                      <TableCell>
                        <span className={`status-badge ${employee.role === 'admin' ? 'status-active' : 'status-pending'}`}>{employee.role}</span>
                      </TableCell>
                      <TableCell>{employee.designation || <span className="text-gray-400">—</span>}</TableCell>
                      <TableCell>
                        <span className={`status-badge ${employee.show_on_website ? 'status-active' : 'status-completed'}`}>
                          {employee.show_on_website ? 'Visible' : 'Hidden'}
                        </span>
                      </TableCell>
                      <TableCell>
                        <span className={`status-badge ${employee.is_active ? 'status-active' : 'status-completed'}`}>
                          {employee.is_active ? 'Active' : 'Inactive'}
                        </span>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex gap-1 justify-end">
                          <Button variant="outline" size="sm" onClick={() => openEditDialog(employee)} data-testid={`edit-employee-${employee.id}`}>
                            <Pencil className="w-3.5 h-3.5" />
                          </Button>
                          {employee.id !== user?.id && (
                            <Button variant="destructive" size="sm" onClick={() => handleDelete(employee)} data-testid={`delete-employee-${employee.id}`}>
                              <Trash2 className="w-3.5 h-3.5" />
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        <div className="mt-6">
          <AuditLinksAdmin />
        </div>
      </div>

      {/* Edit Employee Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={(open) => { if (!open) setEditingEmployee(null); setEditDialogOpen(open); }}>
        <DialogContent data-testid="edit-employee-dialog">
          <DialogHeader>
            <DialogTitle>Edit Employee</DialogTitle>
            <DialogDescription>Update employee details. Leave password blank to keep unchanged.</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleEditSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label>Employee ID</Label>
              <Input value={editFormData.employee_id} onChange={(e) => setEditFormData({ ...editFormData, employee_id: e.target.value })}
                required data-testid="edit-employee-id-input" />
            </div>
            <div className="space-y-2">
              <Label>Name</Label>
              <Input value={editFormData.name} onChange={(e) => setEditFormData({ ...editFormData, name: e.target.value })}
                placeholder="Full name" data-testid="edit-employee-name-input" />
            </div>
            <div className="space-y-2">
              <Label>Mobile Number</Label>
              <Input type="tel" value={editFormData.mobile} onChange={(e) => setEditFormData({ ...editFormData, mobile: e.target.value })}
                required data-testid="edit-mobile-input" />
            </div>
            <div className="space-y-2">
              <Label>Email</Label>
              <Input type="email" value={editFormData.email} onChange={(e) => setEditFormData({ ...editFormData, email: e.target.value })}
                required data-testid="edit-email-input" />
            </div>
            <div className="space-y-2">
              <Label>New Password <span className="text-xs text-slate-400">(leave blank to keep current)</span></Label>
              <Input type="password" value={editFormData.password} onChange={(e) => setEditFormData({ ...editFormData, password: e.target.value })}
                placeholder="Enter new password" data-testid="edit-password-input" />
            </div>
            <div className="space-y-2">
              <Label>Role</Label>
              <select value={editFormData.role} onChange={(e) => setEditFormData({ ...editFormData, role: e.target.value })}
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm" data-testid="edit-role-select">
                <option value="employee">Employee</option>
                <option value="admin">Admin</option>
              </select>
            </div>
            <div className="space-y-2">
              <Label>Designation <span className="text-xs text-slate-400">(shown on website)</span></Label>
              <Input value={editFormData.designation} onChange={(e) => setEditFormData({ ...editFormData, designation: e.target.value })}
                placeholder="e.g. Fleet Supervisor" data-testid="edit-designation-input" />
            </div>
            <div className="space-y-2">
              <Label>Photo <span className="text-xs text-slate-400">{editingEmployee?.photo_url ? '(replace current photo)' : '(shown on website)'}</span></Label>
              <Input type="file" accept="image/*" onChange={(e) => setEditPhotoFile(e.target.files?.[0] || null)} data-testid="edit-photo-input" />
            </div>
            <div className="flex items-center gap-2">
              <input type="checkbox" id="edit-show-on-website" checked={editFormData.show_on_website}
                onChange={(e) => setEditFormData({ ...editFormData, show_on_website: e.target.checked })}
                className="h-4 w-4 rounded border-gray-300" data-testid="edit-show-on-website-checkbox" />
              <Label htmlFor="edit-show-on-website" className="cursor-pointer">Show on website (Our Team section)</Label>
            </div>
            <Button type="submit" className="w-full btn-primary" disabled={submitting} data-testid="submit-edit-employee-btn">
              {submitting && <Loader2 className="w-4 h-4 animate-spin mr-2" />} Update Employee
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminPanel;
